import React, { Suspense, useCallback, useEffect, useState } from 'react';
import http from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ROUTES } from 'Constants/routes';
import { useSnackbar } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { setCurrentUser, login, logout } from 'Actions/auth';
import { setCompanies } from 'Actions/companies';
import MyFlowsPage from 'Pages/users/MyFlowsPage';
import IntegrationsListPage from 'Pages/IntegrationsListPage';
import AdminFlowsPage from 'Pages/AdminFlowsPage';
import EditFlowPage from 'Pages/EditFlowPage';
import LoggingPage from 'Pages/LoggingPage';
import BackgroundFlowsPage from 'Pages/BackgroundFlowsPage';
import MethodsPage from 'Pages/MethodsPage';
import LoanBoardingPage from 'Pages/LoanBoardingPage';
import MethodsParametersPage from 'Pages/MethodsParametersPage';
import PermissionsPage from 'Pages/PermissionsPage';
import ApiClientsPage from 'Pages/ApiClientsPage';
import TelemetryPage from 'Pages/TelemetryPage';
import UsageStatsPage from 'Pages/UsageStatsPage';
import Loader from 'Shared/Loader';
import ProtectedRoute from 'Components/ProtectedRoute';
import { httpSymfony } from 'Utils/http';

//common
const LoginPage = React.lazy(() => import('Pages/auth/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('Pages/auth/ForgotPasswordPage'));
const PasswordResetPage = React.lazy(() => import('Pages/auth/PasswordResetPage'));
const RegistrationPage = React.lazy(() => import('Pages/auth/RegistrationPage'));
const NotFoundPage = React.lazy(() => import('Pages/NotFoundPage'));
const ForbiddenPage = React.lazy(() => import('Pages/ForbiddenPage'));

//admin
const UsersListPage = React.lazy(() => import('Pages/users/UsersListPage'));
const UserDetailsPage = React.lazy(() => import('Pages/users/UserDetailsPage'));
const AdminIntegrationsListPage = React.lazy(() => import('Pages/AdminIntegrationsListPage'));
const AddUserPage = React.lazy(() => import('Pages/auth/AddUserPage'));
const AddIntegrationPage = React.lazy(() => import('Pages/AddIntegrationPage'));

//user
const MyAccountPage = React.lazy(() => import('Pages/users/MyAccountPage'));

const REFRESH_LEAD_TIME = 300000;

// Wrapper component for redirecting to current user's profile
const MyAccountRedirect = () => {
    const currentUserId = useSelector((state) => state.auth.user.id);
    return <Navigate to={`/users/${currentUserId}`} />;
};

const AppRouter = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { enqueueSnackbar } = useSnackbar();

    const access = useSelector((state) => state.auth.user.access);
    const token = useSelector((state) => state.auth.user.token);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

    const [update, setUpdate] = useState(false);
    const [hasError, setHasError] = useState(false);

    const updateToken = useCallback(async () => {
        if (!update) return false;

        try {
            const {
                data: { user, authorisation },
            } = await http.post('/refresh');

            const updatedUser = {
                token: authorisation.token,
                access: user.roles[0]?.name || '',
                ...user,
            };

            // Ensure permissions are properly formatted as array of strings
            if (updatedUser.permissions && Array.isArray(updatedUser.permissions)) {
                updatedUser.permissions = updatedUser.permissions.map(p => typeof p === 'string' ? p : p.name);
            }
            dispatch(login(updatedUser));
        } catch (error) {
            if (hasError) return;
            if (error.response?.status === 401) {
                enqueueSnackbar('Session has expired. Please re-authorize.', {
                    action: CustomSnackbar,
                    variant: 'warning',
                });

                dispatch(logout(navigate));
            } else {
                enqueueSnackbar('An error occurred while refreshing the token.', {
                    action: CustomSnackbar,
                    variant: 'error',
                });
            }
        } finally {
            setUpdate(false);
        }
    }, [update, dispatch, enqueueSnackbar, navigate, hasError]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const setupSymfonyAuth = async () => {
            try {
                // Check for existing token
                const storedToken = localStorage.getItem('token');

                if (storedToken) {
                    const parsedToken = typeof storedToken === 'string' ? storedToken : JSON.parse(storedToken);
                    const cleanToken = parsedToken.replace(/['"]+/g, '');
                    httpSymfony.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
                    return;
                }
            } catch (error) {
                console.error('Failed to setup Symfony authentication:', error);
                // Optional: Handle error (e.g., show notification, retry logic)
            }
        };

        setupSymfonyAuth().then(() => {
        });
        return () => {};
    }, [isAuthenticated, update]);

    useEffect(() => {
        if (sessionStorage.getItem('companies')) {
            const companies = JSON.parse(sessionStorage.getItem('companies'));
            dispatch(setCompanies([...companies]));
            return;
        }
        cachedGet('/companies')
            .then((res) => {
                dispatch(setCompanies([...res.data.companies]));
                sessionStorage.setItem('companies', JSON.stringify(res.data.companies));
            })
            .catch(() => {});
    }, [isAuthenticated, dispatch]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const currentUser = localStorage.getItem('currentUser');
            const currentUserObj = currentUser ? JSON.parse(currentUser) : {};
            const user = {
                token,
                ...currentUserObj,
            };
            if (user.company && !user.company_id) {
                user.company_id = user.company.id;
            }
            // Ensure permissions are properly formatted as array of strings
            if (user.permissions && Array.isArray(user.permissions)) {
                user.permissions = user.permissions.map(p => typeof p === 'string' ? p : p.name);
            }
            dispatch(setCurrentUser(user));
            http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }, [dispatch]);

    useEffect(() => {
        if (update) {
            updateToken().then(() => {
                setHasError(false);
            });
        }
    }, [update, updateToken, setHasError]);

    useEffect(() => {
        if (!token) {
            return;
        }
        let jwt = null;
        try {
            const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            jwt = JSON.parse(atob(payload));
        } catch (error) {
            return;
        }
        if (!jwt.exp) {
            return;
        }

        const lifetime = jwt.iat ? (jwt.exp - jwt.iat) * 1000 : 0;
        const lead = lifetime ? Math.min(REFRESH_LEAD_TIME, lifetime / 2) : REFRESH_LEAD_TIME;
        const timeoutId = setTimeout(() => setUpdate(true), Math.max(jwt.exp * 1000 - lead - Date.now(), 0));

        return () => clearTimeout(timeoutId);
    }, [token]);

    useEffect(() => {
        // Check if http.interceptors.response exists and has the eject method
        if (
            http &&
            http.interceptors &&
            http.interceptors.response &&
            typeof http.interceptors.response.eject === 'function'
        ) {
            const interceptor = http.interceptors.response.use(
                (resp) => resp,
                async (error) => {
                    if (error.response?.status === 401 && !hasError) {
                        setHasError(true);
                        enqueueSnackbar(`Session has expired. Please re-authorize`, {
                            action: CustomSnackbar,
                            variant: 'warning',
                        });
                        dispatch(logout(navigate));
                    }
                    return Promise.reject(error);
                }
            );

            // Cleanup function to remove the interceptor
            return () => {
                http.interceptors.response.eject(interceptor);
            };
        } else {
            console.error(
                'http.interceptors.response.eject is not available. Make sure http and its interceptors are properly set up.'
            );
        }
    }, [dispatch, enqueueSnackbar, navigate, setHasError, hasError]);

    return (
        <Suspense fallback={<Loader />}>
            {isAuthenticated && (
                <Routes>
                    {access === 'User' && (
                        <>
                            <Route path={ROUTES.HOME} exact element={<Navigate to={ROUTES.MY_ACCOUNT} />} />
                            <Route path={ROUTES.LOGIN} exact element={<Navigate to={ROUTES.MY_ACCOUNT} />} />
                            <Route path={ROUTES.MY_ACCOUNT} element={<MyAccountRedirect />} />
                            <Route path={ROUTES.USERS_BY_ID} element={<UserDetailsPage />} />
                            <Route path={ROUTES.INTEGRATIONS} element={
                                <ProtectedRoute permission="MENU_INTEGRATIONS">
                                    <IntegrationsListPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.FLOWS} element={
                                <ProtectedRoute permission="MENU_FLOWS">
                                    <MyFlowsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.FLOW_BY_ID} element={<EditFlowPage />} />
                            <Route path={ROUTES.LOGGING} element={
                                <ProtectedRoute permission="MENU_LOGGING">
                                    <LoggingPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.BACKGROUND_FLOWS} element={
                                <ProtectedRoute permission="MENU_BACKGROUND_FLOWS">
                                    <BackgroundFlowsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.LOAN_BOARDING} element={
                                <ProtectedRoute permission="MENU_LOAN_BOARDING_HUB">
                                    <LoanBoardingPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.PERMISSIONS} element={<PermissionsPage />} />
                        </>
                    )}

                    {access === 'Admin' && (
                        <>
                            <Route path={ROUTES.HOME} exact element={<Navigate to={ROUTES.INTEGRATIONS} />} />
                            <Route path={ROUTES.LOGIN} exact element={<Navigate to={ROUTES.INTEGRATIONS} />} />
                            <Route path={ROUTES.USERS} element={
                                <ProtectedRoute permission="MENU_USER_MANAGMENT">
                                    <UsersListPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.USERS_BY_ID} element={
                                <ProtectedRoute permission="MENU_USER_MANAGMENT">
                                    <UserDetailsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.INTEGRATIONS} element={<AdminIntegrationsListPage />} />
                            <Route path={ROUTES.FLOWS} element={<AdminFlowsPage />} />
                            <Route path={ROUTES.FLOW_BY_ID} element={<EditFlowPage />} />
                            <Route path={ROUTES.FLOW_BY_ID_WITH_CURRENT_USER} element={<EditFlowPage />} />
                            <Route path={ROUTES.LOGGING} element={
                                <ProtectedRoute permission="MENU_LOGGING">
                                    <LoggingPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.BACKGROUND_FLOWS} element={
                                <ProtectedRoute permission="MENU_BACKGROUND_FLOWS">
                                    <BackgroundFlowsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.METHODS_PARAMS} element={<MethodsParametersPage />} />
                            <Route path={ROUTES.METHODS} element={
                                <ProtectedRoute permission="MENU_API_METHODS">
                                    <MethodsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.ADD_USER} element={
                                <ProtectedRoute permission="MENU_USER_MANAGMENT">
                                    <AddUserPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.ADD_INTEGRATION} element={<AddIntegrationPage />} />
                            <Route path={ROUTES.LOAN_BOARDING} element={
                                <ProtectedRoute permission="MENU_LOAN_BOARDING_HUB">
                                    <LoanBoardingPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.PERMISSIONS} element={<PermissionsPage />} />
                            <Route path={ROUTES.TELEMETRY} element={<TelemetryPage />} />
                            <Route path={ROUTES.USAGE_STATS} element={<UsageStatsPage />} />
                            <Route path={ROUTES.API_CLIENTS} element={<ApiClientsPage />} />
                        </>
                    )}

                    {access === 'Company Admin' && (
                        <>
                            <Route path={ROUTES.HOME} exact element={<Navigate to={ROUTES.MY_ACCOUNT} />} />
                            <Route path={ROUTES.LOGIN} exact element={<Navigate to={ROUTES.MY_ACCOUNT} />} />
                            <Route path={ROUTES.MY_ACCOUNT} element={<MyAccountRedirect />} />
                            <Route path={ROUTES.USERS} element={
                                <ProtectedRoute permission="MENU_USER_MANAGMENT">
                                    <UsersListPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.USERS_BY_ID} element={
                                <ProtectedRoute permission="MENU_USER_MANAGMENT">
                                    <UserDetailsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.INTEGRATIONS} element={
                                <ProtectedRoute permission="MENU_INTEGRATIONS">
                                    <IntegrationsListPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.FLOWS} element={<AdminFlowsPage />} />
                            <Route path={ROUTES.FLOW_BY_ID} element={<EditFlowPage />} />
                            <Route path={ROUTES.FLOW_BY_ID_WITH_CURRENT_USER} element={<EditFlowPage />} />
                            <Route path={ROUTES.LOGGING} element={
                                <ProtectedRoute permission="MENU_LOGGING">
                                    <LoggingPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.BACKGROUND_FLOWS} element={
                                <ProtectedRoute permission="MENU_BACKGROUND_FLOWS">
                                    <BackgroundFlowsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.METHODS_PARAMS} element={
                                <ProtectedRoute permission="MENU_METHODS_PARAMS">
                                    <MethodsParametersPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.METHODS} element={
                                <ProtectedRoute permission="MENU_API_METHODS">
                                    <MethodsPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.ADD_USER} element={
                                <ProtectedRoute permission="MENU_USER_MANAGMENT">
                                    <AddUserPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.ADD_INTEGRATION} element={<AddIntegrationPage />} />
                            <Route path={ROUTES.LOAN_BOARDING} element={
                                <ProtectedRoute permission="MENU_LOAN_BOARDING_HUB">
                                    <LoanBoardingPage />
                                </ProtectedRoute>
                            } />
                            <Route path={ROUTES.PERMISSIONS} element={<PermissionsPage />} />
                            <Route path={ROUTES.API_CLIENTS} element={<ApiClientsPage />} />
                        </>
                    )}

                    {/* all authenticated */}

                    <Route path={ROUTES.PASSWORD_RESET} element={<PasswordResetPage />} />
                    <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
                    <Route path={ROUTES.ALL} element={<NotFoundPage />} />
                    <Route path={ROUTES.FORBIDDEN_403} element={<ForbiddenPage />} />
                </Routes>
            )}

            {/* all access */}
            {!isAuthenticated && (
                <Routes>
                    <Route path={ROUTES.HOME} exact element={<Navigate to={ROUTES.LOGIN} />} />
                    <Route path={ROUTES.LOGIN} exact element={<LoginPage />} />
                    <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
                    <Route path={ROUTES.REGISTRATION} element={<RegistrationPage />} />
                    <Route path={ROUTES.PASSWORD_RESET} element={<PasswordResetPage />} />
                    <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
                    <Route path={ROUTES.ALL} element={<NotFoundPage />} />
                </Routes>
            )}
        </Suspense>
    );
};

export default AppRouter;
